import { FeatureName, useFeatureOn } from "common/Split";

import React, { useState } from "react";
import { ReplenishmentConstants } from "transfers/constants/ReplenishmentConstants";
import { SelectEcomDestination } from "../SelectEcomDestination";
import { includes } from "lodash";
import { selectTransferCreate } from "transfers/create/store/TransferCreateSelectors";
import { transfersShipmentCreateLabels } from "transfers/create/transfersShipmentCreate.labels";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { DistributionChannel } from "@deliverr/replenishment-client";

interface UseTransferEcomDestinationResult {
  value: DistributionChannel;
  content: JSX.Element | string;
}

export const useTransferEcomDestination = (
  onEomDestinationChange: (value: DistributionChannel) => void
): UseTransferEcomDestinationResult => {
  const { formatMessage } = useIntl();
  const { distributionChannel } = useSelector(selectTransferCreate);
  const isEcomDirectEnabled = useFeatureOn(FeatureName.SellerEnabledForRsToEcomDirectTransfer);
  const isEcomForwardingEnabled = useFeatureOn(FeatureName.SellerEnabledForRsToEcomForwardingPlan);

  const defaultEcomDestination = isEcomDirectEnabled
    ? DistributionChannel.ECOM_DIRECT_TO_FC
    : DistributionChannel.ECOM_VIA_CROSSDOCK;

  const [selectedEcomDestination, setSelectedEcomDestination] = useState(
    includes(ReplenishmentConstants.ECOM_CHANNELS, distributionChannel) ? distributionChannel : defaultEcomDestination
  );

  const handleEcomDestinationChange = (value: DistributionChannel) => {
    setSelectedEcomDestination(value);
    onEomDestinationChange(value);
  };

  if (isEcomDirectEnabled && isEcomForwardingEnabled) {
    return {
      value: selectedEcomDestination ?? defaultEcomDestination,
      content: (
        <SelectEcomDestination
          selectedDestination={selectedEcomDestination}
          onChange={handleEcomDestinationChange}
          destinations={[DistributionChannel.ECOM_VIA_CROSSDOCK, DistributionChannel.ECOM_DIRECT_TO_FC]}
        />
      ),
    };
  }

  return {
    value: defaultEcomDestination,
    content: formatMessage(transfersShipmentCreateLabels.steps.destination.options.ecom.description),
  };
};
